import { useEffect, useState } from "react";

import { Link } from "react-router";

import { Pagination } from "@/components/pieces/form/Pagination";
import { ArticleCard } from "@/components/pieces/learn/ArticleCard";
import { useResponsiveGridSize } from "@/components/pieces/learn/useResponsiveGridSize";
import {
  ArticlePreviewFragment,
  ArticleReadStatusReadStatus,
  useRelatedSeriesArticlesLazyQuery,
} from "@/graphql";

type Article = ArticlePreviewFragment;

interface ArticleSeriesGridProps {
  seriesId: string;
  seriesSlug: string;
  title: string;
  initialArticles?: Article[];
  totalCount: number;
  readStatusById?: Record<string, ArticleReadStatusReadStatus>;
  showLink?: boolean;
  cardsPerPage?: number;
}

export function ArticleSeriesGrid({
  seriesSlug,
  title,
  initialArticles = [],
  totalCount,
  readStatusById = {},
  showLink = true,
  cardsPerPage = 4,
}: ArticleSeriesGridProps) {
  const [articles, setArticles] = useState(initialArticles);
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerRow = useResponsiveGridSize();

  const [fetchArticles, { loading }] = useRelatedSeriesArticlesLazyQuery({
    onCompleted: (data) => {
      if (data?.articleSeries?.articles) {
        setArticles(data.articleSeries.articles);
      }
    },
  });

  const totalPages = Math.ceil(totalCount / cardsPerPage);
  const hasInitialArticles = initialArticles.length > 0;

  useEffect(() => {
    // Only fetch if we don't have initial articles or if we're paginating
    if (!hasInitialArticles || currentPage > 1) {
      fetchArticles({
        variables: {
          slug: seriesSlug,
          limit: cardsPerPage,
          offset: (currentPage - 1) * cardsPerPage,
        },
      });
    }
  }, [currentPage, seriesSlug, cardsPerPage]); // Don't include fetchArticles or hasInitialArticles to avoid loops

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="w-full mb-12">
      {/* Header with title and pagination */}
      <div className="flex justify-between items-baseline mb-6">
        <div className="pr-2">
          {showLink ? (
            <Link
              to={`/learn/series/${seriesSlug}`}
              className="text-lg md:text-2xl font-semibold text-gray-900 hover:text-blue-600 transition-colors"
            >
              {title}
            </Link>
          ) : (
            <h3 className="text-lg md:text-2xl font-semibold text-gray-900">
              {title}
            </h3>
          )}
        </div>

        {totalPages > 1 && (
          <Pagination
            nbPages={totalPages}
            currentPage={currentPage}
            totalCount={totalCount}
            countPerPage={cardsPerPage}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}
      </div>

      {/* Article grid */}
      <div
        className={`grid gap-6 w-full transition-opacity duration-200 ${
          loading ? "opacity-50" : "opacity-100"
        }`}
        style={{
          gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
          minHeight: loading && articles.length > 0 ? "20rem" : undefined,
        }}
      >
        {loading && articles.length === 0
          ? // Loading skeleton for initial load
            Array.from({ length: cardsPerPage }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="h-64 bg-gray-100 rounded-lg animate-pulse"
              />
            ))
          : articles.map((article) => (
              <div key={article.id} className="min-w-0 h-full">
                <ArticleCard
                  item={{
                    ...article,
                    currentUserReadStatus: readStatusById[article.id],
                  }}
                />
              </div>
            ))}
      </div>
    </div>
  );
}
